import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from '@tanstack/react-router';
import { loginSchema, type LoginInput } from '@ddp/shared';
import Button from '@carbon/react/es/components/Button';
import Form from '@carbon/react/es/components/Form/Form';
import { Stack } from '@carbon/react/es/components/Stack/Stack';
import TextInput from '@carbon/react/es/components/TextInput';
import PasswordInput from '@carbon/react/es/components/TextInput/PasswordInput';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';

import { useAuthStore } from '../auth/store.js';
import { zodResolver } from '../lib/zod-resolver.js';
import { AuthCard } from './AuthCard.js';

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await login(values);
      await navigate({ to: '/' });
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Connexion impossible.');
    }
  });

  return (
    <AuthCard
      title="Connexion"
      footer={
        <>
          Pas encore de compte ? <RouterLink to="/register">Créer un compte</RouterLink>
        </>
      }
    >
      <Form onSubmit={onSubmit}>
        <Stack gap={6}>
          {formError && (
            <InlineNotification
              kind="error"
              title="Échec de la connexion"
              subtitle={formError}
              lowContrast
              hideCloseButton
            />
          )}
          <TextInput
            id="email"
            type="email"
            labelText="Adresse e-mail"
            autoComplete="email"
            invalid={Boolean(errors.email)}
            invalidText={errors.email?.message}
            {...register('email')}
          />
          <PasswordInput
            id="password"
            labelText="Mot de passe"
            autoComplete="current-password"
            showPasswordLabel="Afficher le mot de passe"
            hidePasswordLabel="Masquer le mot de passe"
            invalid={Boolean(errors.password)}
            invalidText={errors.password?.message}
            {...register('password')}
          />
          <Button type="submit" disabled={isSubmitting}>
            Se connecter
          </Button>
        </Stack>
      </Form>
    </AuthCard>
  );
}
